import React from 'react';
import PropTypes from 'prop-types';

import { HeadingStyled } from './Heading.styled';

export const headingVariant = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
  ERROR: 'destructive',
  WARNING: 'warning',
  SUCCESS: 'success',
  MINIMAL: 'minimal',
};

export const headingSize = {
  XSMALL: 'xsmall',
  SMALL: 'small',
  DEFAULT: 'default',
  MEDIUM: 'medium',
  XMEDIUM: 'xmedium',
  LARGE: 'large',
  XLARGE: 'xlarge',
  XXLARGE: 'xxlarge',
  XXXLARGE: 'xxxlarge',
};

export const headingLevel = {
  H1: 1,
  H2: 2,
  H3: 3,
  H4: 4,
  H5: 5,
  H6: 6,
};

const levelPropList = () => {
  const values = Object.values(headingLevel);
  return [...values, ...values.map(n => `${n}`)];
};

export const headingWeight = {
  REGULAR: 'regular',
  XBOLD: 'xbold',
};

export const headingAlign = {
  DEFAULT: 'initial',
  LEFT: 'left',
  RIGHT: 'right',
  CENTER: 'center',
  JUSTIFY: 'justify',
  INHERIT: 'inherit',
};

export const headingTransform = {
  DEFAULT: 'none',
  UPPERCASE: 'uppercase',
  LOWERCASE: 'lowercase',
  CAPITALIZE: 'capitalize',
};

export default function Heading(props) {
  if (!props.children && props.text) {
    return (
      <HeadingStyled
        as={`h${props.level}`}
        dangerouslySetInnerHTML={{ __html: props.text }}
        {...props}
      />
    );
  }
  return (
    <HeadingStyled as={`h${props.level}`} {...props}>
      {props.children}
    </HeadingStyled>
  );
}

Heading.defaultProps = {
  level: headingLevel.H1,
  variant: headingVariant.PRIMARY,
  isTruncated: false,
  weight: headingWeight.XBOLD,
  align: headingAlign.DEFAULT,
  textTransform: headingTransform.DEFAULT,
};

Heading.propTypes = {
  mr: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
  textDecoration: PropTypes.string,
  /**
   * Heading text
   */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * Number to generate Heading level between 1-6
   */
  level: PropTypes.oneOf(levelPropList()),
  /**
   * Defines different appearances for the component
   */
  variant: PropTypes.oneOf(Object.values(headingVariant)),
  /**
   * The same as font-size CSS property using a list of predefined sizes
   */
  size: PropTypes.oneOf(Object.values(headingSize)),
  /**
   * The same as margin CSS property using a list of predefined sizes (theme.spacing). For example 'default 0' will be the same as '1rem 0'. The theme.spacing keys will be replaced by the correspondent value.
   */
  margin: PropTypes.string,
  /**
   * Showing truncated text
   */
  isTruncated: PropTypes.bool,
  /**
   * Defines the font weight, regular === 300, and xbold === 900
   */
  weight: PropTypes.oneOf(Object.values(headingWeight)),
  /**
   * The same as text-align CSS property
   */
  align: PropTypes.oneOf(Object.values(headingAlign)),
  /**
   * The same as text-transform CSS property
   */
  textTransform: PropTypes.oneOf(Object.values(headingTransform)),
  /**
   * The same as white-space CSS property set to 'nowrap' if it is true
   */
  singleLine: PropTypes.bool,
};
